import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { normalizeCatalogProduct } from "../lib/catalogProduct";

async function loadStaticCatalogShell({ destination, currency, marketplace, category, search }) {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  const res = await fetch(`${base}/products.json`, {
    headers: { "Cache-Control": "no-cache" },
  });
  if (!res.ok) throw new Error(`Static catalog HTTP ${res.status}`);
  const data = await res.json();
  const all = Array.isArray(data)
    ? data
    : Array.isArray(data?.products)
      ? data.products
      : [];

  let filtered = all;
  if (marketplace && marketplace !== "all") {
    filtered = filtered.filter(
      (p) => (p.marketplace || "").toLowerCase() === marketplace.toLowerCase()
    );
  }
  if (category && category !== "all") {
    filtered = filtered.filter(
      (p) => (p.category || "").toLowerCase() === category.toLowerCase()
    );
  }
  if (search?.trim()) {
    const q = search.trim().toLowerCase();
    filtered = filtered.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
    );
  }

  const products = filtered.map((p) =>
    normalizeCatalogProduct(
      { ...p, is_catalog_image: true, is_verified_image: false },
      { destination, currency }
    )
  );
  const categories = [...new Set(all.map((p) => p.category).filter(Boolean))].sort();

  return { products, total: products.length, categories };
}

/**
 * Hybrid catalog: instant products.json shell, then live DHC /api/products upgrade.
 */
export function useLiveCatalog({
  api,
  destination,
  currency,
  marketplace,
  category,
  search,
}) {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [showcasePolicy, setShowcasePolicy] = useState("");
  const [catalogMeta, setCatalogMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [liveReady, setLiveReady] = useState(false);
  const requestId = useRef(0);
  const shellLoadedRef = useRef(false);

  const fetchCatalog = useCallback(
    async (isRefresh = false) => {
      const id = ++requestId.current;
      if (isRefresh) setRefreshing(true);
      else {
        setLoading(true);
        shellLoadedRef.current = false;
      }
      setError(null);

      if (!isRefresh) {
        try {
          const shell = await loadStaticCatalogShell({
            destination,
            currency,
            marketplace,
            category,
            search,
          });
          if (id === requestId.current && shell.products.length > 0) {
            setProducts(shell.products);
            setTotal(shell.total);
            setCategories(shell.categories);
            shellLoadedRef.current = true;
            setLoading(false);
          }
        } catch {
          /* static shell optional */
        }
      }

      try {
        const params = {
          destination,
          currency,
          limit: 100,
          _t: Date.now(),
        };
        if (marketplace && marketplace !== "all") params.marketplace = marketplace;
        if (category && category !== "all") params.category = category;
        if (search?.trim()) params.search = search.trim();

        const { data } = await axios.get(`${api}/products`, {
          params,
          headers: { "Cache-Control": "no-cache" },
          timeout: 25000,
        });

        if (id !== requestId.current) return;

        const rows = Array.isArray(data?.products) ? data.products : [];
        setProducts(
          rows.map((p) => normalizeCatalogProduct(p, { destination, currency }))
        );
        setTotal(Number(data?.total) || rows.length);
        setCategories(Array.isArray(data?.categories) ? data.categories : []);
        setShowcasePolicy(data?.showcase_policy || "");
        setCatalogMeta({
          revision: data?.catalog_revision,
          updatedAt: data?.catalog_updated_at,
          inSync: data?.catalog_in_sync,
        });
        setLiveReady(true);
        setError(null);
      } catch (err) {
        if (id !== requestId.current) return;
        if (!liveReady && !shellLoadedRef.current) {
          const message =
            err?.response?.data?.detail ||
            err?.response?.data?.error ||
            err?.message ||
            "Failed to load products";
          setError(new Error(message));
        }
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [api, destination, currency, marketplace, category, search, liveReady]
  );

  useEffect(() => {
    fetchCatalog(false);
  }, [fetchCatalog]);

  const refresh = useCallback(() => fetchCatalog(true), [fetchCatalog]);

  return {
    products,
    total,
    categories,
    showcasePolicy,
    catalogMeta,
    loading,
    refreshing,
    error,
    liveReady,
    refresh,
  };
}
